import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { SiteShell } from "@/components/SiteShell";
import { PageHero } from "@/components/PageHero";
import { useLocale } from "@/contexts/LocaleContext";
import {
  JOURNAL_POSTS,
  getPostBySlug,
  getRelatedPosts,
  getCategoryLabel,
} from "@/data/journal-posts";

const SITE_URL = PUBLIC_SITE_ORIGIN;

import { publicCacheHeaders } from "@/lib/public-cache-headers";

export const Route = createFileRoute("/journal/$slug")({
  headers: publicCacheHeaders,
  loader: ({ params }) => {
    const post = getPostBySlug(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post;
    if (!post) return { meta: [{ title: "Journal — Lecmo" }] };
    const en = post.en;
    const url = `${SITE_URL}/journal/${post.slug}`;
    const image = `${SITE_URL}${post.coverImage}`;
    return {
      meta: [
        { title: `${en.title} — Lecmo Journal` },
        { name: "description", content: en.excerpt },
        { property: "og:type", content: "article" },
        { property: "og:title", content: en.title },
        { property: "og:description", content: en.excerpt },
        { property: "og:image", content: image },
        { property: "og:url", content: url },
        { property: "article:published_time", content: post.publishedAt },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: en.title },
        { name: "twitter:description", content: en.excerpt },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: en.title,
            description: en.excerpt,
            image: [image],
            datePublished: post.publishedAt,
            dateModified: post.publishedAt,
            author: { "@type": "Organization", name: "Lecmo Editorial" },
            publisher: {
              "@type": "Organization",
              name: "Lecmo",
              logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.ico` },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            ...(post.sources.length > 0 && {
              citation: post.sources.map((s) => ({
                "@type": "CreativeWork",
                name: s.title,
                url: s.url,
                ...(s.publisher && { publisher: { "@type": "Organization", name: s.publisher } }),
              })),
            }),
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <SiteShell>
      <div className="mx-auto w-full max-w-2xl px-4 py-32 text-center sm:px-6">
        <h1 className="t-h2">Article not found</h1>
        <p className="mt-4 text-muted-foreground">This journal entry does not exist.</p>
        <Link to="/journal" className="mt-8 inline-block underline">
          Back to Journal
        </Link>
      </div>
    </SiteShell>
  ),
  component: JournalArticlePage,
});

function JournalArticlePage() {
  const { post } = Route.useLoaderData();
  const { locale } = useLocale();
  const lang = locale === "ar" ? "ar" : "en";
  const content = post[lang];
  const related = getRelatedPosts(post.slug, 2);

  return (
    <SiteShell>
      <PageHero
        title={content.title}
        subtitle={content.excerpt}
        image={post.coverImage}
        alt={content.title}
      />

      <article className="mx-auto w-full max-w-3xl px-4 py-20 sm:px-6">


        <p className="t-eyebrow tracking-[var(--tracking-wider)] text-muted-foreground mt-8">
          {getCategoryLabel(post.category, lang)} · {post.minutes} {lang === "ar" ? "دقائق" : "min read"} ·{" "}
          {new Date(post.publishedAt).toLocaleDateString(lang === "ar" ? "ar" : "en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            timeZone: "UTC",
          })}
        </p>

        <div className="prose prose-neutral dark:prose-invert mt-8 max-w-none prose-headings:font-serif prose-headings:tracking-[-0.01em] prose-h2:mt-14 prose-h2:mb-4 prose-h2:text-3xl prose-p:font-serif prose-p:text-[17px] prose-p:leading-[1.85] prose-a:text-foreground prose-a:underline-offset-4">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.body}</ReactMarkdown>
        </div>

        {post.sources.length > 0 && (
          <section className="mt-16 border-t border-border pt-10">
            <h2 className="t-h3 mb-6">{lang === "ar" ? "المصادر" : "Sources"}</h2>
            <ul className="space-y-3">
              {post.sources.map((s: { title: string; url: string; publisher?: string }) => (
                <li key={s.url} className="text-sm">
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="underline underline-offset-4 hover:text-foreground"
                  >
                    {s.title}
                  </a>
                  {s.publisher && (
                    <span className="text-muted-foreground"> — {s.publisher}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {related.length > 0 && (
          <section className="mt-16 border-t border-border pt-10">
            <h2 className="t-h3 mb-6">
              {lang === "ar" ? "تابع القراءة" : "Continue reading"}
            </h2>
            <div className="grid gap-8 md:grid-cols-2">
              {related.map((r) => {
                const rc = r[lang];
                return (
                  <Link
                    key={r.slug}
                    to="/journal/$slug"
                    params={{ slug: r.slug }}
                    className="group block"
                  >
                    <div className="aspect-[4/5] overflow-hidden bg-secondary">
                      <img
                        src={r.coverImage}
                        alt={rc.title}
                        width={1280}
                        height={1600}
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="mt-4 t-h3">{rc.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{rc.excerpt}</p>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </article>
    </SiteShell>
  );
}

void JOURNAL_POSTS;
